/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/classOfServicesApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API service functions for Class of Services.
────────────────────────────────────────────────────────────────*/

import { requestTelephonyModule, CLASS_OF_SERVICES_ENDPOINT, FEATURE_CODE_COS_ENDPOINT, DIAL_RULE_RESTRICTIONS_ENDPOINT, ARS_ENDPOINT } from '../api';
import {
  ClassOfService,
  ListClassOfServicesParams,
  ListClassOfServicesResponse,
  CreateClassOfServicePayload,
  UpdateClassOfServicePayload,
  FeatureCodeCategory,
  DialRuleRestriction,
  ArsProfile
} from '../types';

const QUICK_LIST_LIMIT = 1000;

/**
 * Fetch Class of Services with pagination and filters
 */
export async function fetchClassOfServices(
  params: ListClassOfServicesParams,
  t: (key: string) => string
): Promise<ListClassOfServicesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search ? { search: params.search } : {}),
    ...(params.domainId != null ? { domainId: params.domainId.toString() } : {}),
  });

  const { data: rawData, pagination: paginationMeta } = await requestTelephonyModule<ClassOfService[]>(
    `${CLASS_OF_SERVICES_ENDPOINT}?${queryParams.toString()}`,
    t('classOfServices.messages.loadError')
  );

  const data: ClassOfService[] = Array.isArray(rawData) ? rawData : [];
  const pagination = paginationMeta || {};

  return {
    success: true,
    data,
    pagination: {
      page: pagination.page ?? params.page,
      limit: pagination.limit ?? params.limit,
      total: pagination.total ?? data.length,
      totalPages: pagination.totalPages ?? 0,
    },
  };
}

/**
 * Fetch single Class of Service by ID
 */
export async function fetchClassOfServiceById(
  id: string,
  t: (key: string) => string
): Promise<ClassOfService> {
  const { data: payload } = await requestTelephonyModule<ClassOfService>(
    `${CLASS_OF_SERVICES_ENDPOINT}/${id}`,
    t('classOfServices.messages.loadError')
  );

  if (!payload) {
    throw new Error(t('classOfServices.messages.loadError'));
  }

  return payload;
}

/**
 * Create new Class of Service
 */
export async function createClassOfService(
  payload: CreateClassOfServicePayload,
  t: (key: string) => string
): Promise<{ success: boolean; data: ClassOfService & { id?: number }; message?: string }> {
  const trimmedPayload = {
    ...payload,
    name: payload.name.trim(),
    description: payload.description?.trim() || '',
    featureCodeCosId: payload.featureCodeCosId ?? null,
    dialRuleRestrictionId: payload.dialRuleRestrictionId ?? null,
    arsId: payload.arsId ?? null,
  };

  const { data: resultData } = await requestTelephonyModule<Partial<ClassOfService> & { id?: number }>(
    CLASS_OF_SERVICES_ENDPOINT,
    t('classOfServices.messages.createError'),
    {
      method: 'POST',
      body: JSON.stringify(trimmedPayload),
    }
  );

  return {
    success: true,
    data: (resultData || { ...payload, id: resultData?.id }) as ClassOfService & { id?: number },
  };
}

/**
 * Update Class of Service
 */
export async function updateClassOfService(
  id: string,
  payload: UpdateClassOfServicePayload,
  t: (key: string) => string
): Promise<{ success: boolean; data: ClassOfService; message?: string }> {
  const trimmedPayload = {
    ...payload,
    name: payload.name?.trim(),
    description: payload.description?.trim() || '',
    featureCodeCosId: payload.featureCodeCosId ?? null,
    dialRuleRestrictionId: payload.dialRuleRestrictionId ?? null,
    arsId: payload.arsId ?? null,
  };

  const { data: resultData } = await requestTelephonyModule<ClassOfService>(
    `${CLASS_OF_SERVICES_ENDPOINT}/${id}`,
    t('classOfServices.messages.updateError'),
    {
      method: 'PUT',
      body: JSON.stringify(trimmedPayload),
    }
  );

  if (!resultData) {
    throw new Error('Failed to update Class of Service - no data returned');
  }

  return {
    success: true,
    data: resultData,
  };
}

/**
 * Delete Class of Service
 */
export async function deleteClassOfService(
  id: string,
  t: (key: string) => string
): Promise<void> {
  await requestTelephonyModule<null>(
    `${CLASS_OF_SERVICES_ENDPOINT}/${id}`,
    t('classOfServices.messages.deleteError'),
    { method: 'DELETE' }
  );
}

/**
 * Fetch Feature Code Categories
 */
export async function fetchFeatureCodeCategories(
  t: (key: string) => string
): Promise<FeatureCodeCategory[]> {
  const { data } = await requestTelephonyModule<FeatureCodeCategory[]>(
    FEATURE_CODE_COS_ENDPOINT,
    t('classOfServices.messages.optionsLoadError')
  );

  return Array.isArray(data) ? data : [];
}

/**
 * Fetch Dial Rule Restrictions
 */
export async function fetchDialRuleRestrictions(
  t: (key: string) => string
): Promise<DialRuleRestriction[]> {
  const { data } = await requestTelephonyModule<DialRuleRestriction[]>(
    DIAL_RULE_RESTRICTIONS_ENDPOINT,
    t('classOfServices.messages.optionsLoadError')
  );

  return Array.isArray(data) ? data : [];
}

/**
 * Fetch ARS Profiles
 */
export async function fetchArsProfiles(
  t: (key: string) => string
): Promise<ArsProfile[]> {
  const { data } = await requestTelephonyModule<ArsProfile[]>(
    `${ARS_ENDPOINT}?limit=${QUICK_LIST_LIMIT}`,
    t('classOfServices.messages.optionsLoadError')
  );

  return Array.isArray(data) ? data : [];
}

/**
 * Fetch Class of Services for Quick List
 */
export async function fetchClassOfServicesForQuickList(
  t: (key: string) => string
): Promise<ClassOfService[]> {
  const { data } = await requestTelephonyModule<ClassOfService[]>(
    `${CLASS_OF_SERVICES_ENDPOINT}?limit=${QUICK_LIST_LIMIT}`,
    t('classOfServices.messages.quickListError')
  );

  return Array.isArray(data) ? data : [];
}

